/**
 * MyActivityLog — Customer-facing lifetime activity stream.
 *
 * Renders the events captured across all the member's leads
 * (events flagged customer_visible=true). Used inside AccountPage.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Clock, FileText, Phone, Mail, MessageSquare, MapPin, Sparkles,
  CheckCircle2, ChevronRight, Loader2, Inbox, X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_META = {
  stage_changed: { Icon: ChevronRight, label: 'Status updated', tone: 'text-blue-300' },
  note_added: { Icon: FileText, label: 'Note', tone: 'text-text-secondary' },
  call_logged: { Icon: Phone, label: 'Call', tone: 'text-cyan-300' },
  email_sent_via_dreamlife: { Icon: Mail, label: 'Email', tone: 'text-cyan-300' },
  sms_sent_via_dreamlife: { Icon: MessageSquare, label: 'Message', tone: 'text-cyan-300' },
  quotation_uploaded: { Icon: FileText, label: 'Quotation shared', tone: 'text-amber-300' },
  site_visit_scheduled: { Icon: MapPin, label: 'Site visit scheduled', tone: 'text-purple-300' },
  contact_reveal_requested: { Icon: Phone, label: 'Contact requested', tone: 'text-gold' },
  contact_revealed: { Icon: Phone, label: 'Contact shared', tone: 'text-gold' },
  customer_message: { Icon: MessageSquare, label: 'You messaged', tone: 'text-emerald-300' },
  customer_viewed_quotation: { Icon: CheckCircle2, label: 'You viewed a quotation', tone: 'text-emerald-300' },
  customer_accepted_quotation: { Icon: CheckCircle2, label: 'You accepted a quotation', tone: 'text-emerald-400' },
  customer_requested_callback: { Icon: Phone, label: 'You requested a callback', tone: 'text-emerald-300' },
  customer_cancelled: { Icon: X, label: 'You cancelled', tone: 'text-red-300' },
  lead_assigned: { Icon: Sparkles, label: 'Concierge assigned a partner', tone: 'text-gold' },
  lead_unassigned: { Icon: X, label: 'Partner unassigned', tone: 'text-text-secondary' },
  lead_reopened: { Icon: Sparkles, label: 'Lead reopened', tone: 'text-blue-300' },
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const y = new Date(today); y.setDate(today.getDate() - 1);
    if (sameDay) return `Today at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    if (d.toDateString() === y.toDateString()) return `Yesterday at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    return d.toLocaleString();
  } catch { return iso; }
};

const MyActivityLog = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/leads/me/activity`);
        if (!cancelled) setEvents(data.events || []);
      } catch {
        // soft-fail — section just stays hidden
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="glass-card p-6 flex items-center gap-3 text-text-secondary text-sm" data-testid="activity-log-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your activity…
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="glass-card p-8 text-center" data-testid="activity-log-empty">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/5 mb-3">
          <Inbox className="w-5 h-5 text-text-secondary" />
        </div>
        <h3 className="font-semibold mb-1">Your journey starts here</h3>
        <p className="text-text-secondary text-sm">
          Every interaction with ASTIIV BASGAT and our verified partners will appear here,
          so you always have a complete record of your luxury journey.
        </p>
      </div>
    );
  }

  const visible = showAll ? events : events.slice(0, 8);

  return (
    <div className="glass-card p-6" data-testid="activity-log">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gold" />
          <h3 className="font-playfair text-xl font-semibold">My Activity</h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-text-secondary">
          {events.length} {events.length === 1 ? 'event' : 'events'}
        </span>
      </div>

      <ol className="relative border-l border-white/10 pl-5 space-y-4">
        {visible.map((ev, i) => {
          const meta = EVENT_META[ev.event_type] || EVENT_META.note_added;
          const { Icon } = meta;
          return (
            <motion.li
              key={`${ev.created_at}-${i}`}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03, duration: 0.3 }}
              className="relative"
              data-testid={`activity-event-${i}`}
            >
              <span className="absolute -left-[27px] w-5 h-5 rounded-full bg-[#1a1a1a] ring-2 ring-gold/30 flex items-center justify-center">
                <Icon className={`w-2.5 h-2.5 ${meta.tone}`} />
              </span>
              <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3 hover:border-white/10 transition-colors">
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${meta.tone}`}>
                    {meta.label}
                  </span>
                  <span className="text-[10px] text-text-secondary">{formatTime(ev.created_at)}</span>
                </div>
                {ev.description && (
                  <p className="text-sm text-white/85 leading-relaxed">{ev.description}</p>
                )}
                {ev.actor_name && ev.actor_role !== 'customer' && (
                  <div className="mt-1.5 text-[11px] text-text-secondary">
                    via <span className="text-white/70">{ev.actor_name}</span>
                  </div>
                )}
              </div>
            </motion.li>
          );
        })}
      </ol>

      {events.length > 8 && (
        <button
          onClick={() => setShowAll((v) => !v)}
          className="mt-5 text-xs text-gold hover:text-gold/80 uppercase tracking-[0.2em]"
          data-testid="activity-toggle-all"
        >
          {showAll ? 'Show recent only' : `Show all ${events.length} events`}
        </button>
      )}
    </div>
  );
};

export default MyActivityLog;
